import { CSSProperties } from "react";
import generalModule from "../../../../utilities/GeneralModule";
import { ComponentTypes, DirectoryTreeType } from "../../config/config";
import { IFolderPanelInfo } from "../../interfaces/IFolderPanelInfo";
import { IPageSettings } from "../../interfaces/IPageSettings";
import { ITeamChannelDocumentLibrary } from "../../interfaces/services/ITeamChannelDocumentLibrary";
import { getTeamChannelInfo } from "../../services/GraphService";
import { isMobile } from "../../styles/mobileStyles";
import { UserState } from "../../types";
import { FolderTypeEnum } from "../enums/FolderTypeEnum";
import { getLastSplittedStringItem, getRelativeDocumentUrl } from "./stringHelper";
import { tryGetSiteRelativeUrl } from "./UrlHelper";

export const findWorkingDirectories = (data: any[], selectedItem: any): any[] | null => {
    for (const item of data) {
        if (item.id === selectedItem.id && item.randomId === selectedItem.randomId) {
            return [item];
        }
        if (item.sites) {
            const found = findWorkingDirectories(item.sites, selectedItem);
            if (found) {
                return [item, ...found];
            }
        }
        if (item.subSites) {
            const found = findWorkingDirectories(item.subSites, selectedItem);
            if (found) {
                return [item, ...found];
            }
        }
        if (item.documents) {
            const found = findWorkingDirectories(item.documents, selectedItem);
            if (found) {
                return [item, ...found];
            }
        }
        if (item.channels) {
            const found = findWorkingDirectories(item.channels, selectedItem);
            if (found) {
                return [item, ...found];
            }
        }
        if (item.folders) {
            const found = findWorkingDirectories(item.folders, selectedItem);
            if (found) {
                return [item, ...found];
            }
        }
        if (item.accounts) {
            const found = findWorkingDirectories(item.accounts, selectedItem);
            if (found) {
                return [item, ...found];
            }
        }
        if (item.projectFolders) {
            const found = findWorkingDirectories(item.projectFolders, selectedItem);
            if (found) {
                return [item, ...found];
            }
        }
        if (item.projects) {
            const found = findWorkingDirectories(item.projects, selectedItem);
            if (found) {
                return [item, ...found];
            }
        }
    }
    return null;
}

export const buildWorkingDirectoryFromTree = async (data: any[], user: UserState) => {
    let directories: IPageSettings[] = [];
    let currentDirectory: IPageSettings;
    let teamChannelInfo: ITeamChannelDocumentLibrary;

    const sharepointItem = { type: ComponentTypes.SharePoint, id: "1", directory: "", displayName: ComponentTypes.SharePoint, webUrl: "" }
    const teamsItem = { type: ComponentTypes.Teams, id: "2", directory: "", displayName: ComponentTypes.Teams, webUrl: "" }

    const rootItem = data[0];
    if (rootItem.treeType !== DirectoryTreeType.location) {
        if (rootItem.isSite) {
            if (!generalModule.isInConentMangerMode()) {
                if (rootItem.treeType === DirectoryTreeType.tessAccount) {
                    directories.push({ type: ComponentTypes.Tess, id: "5", directory: "", displayName: ComponentTypes.Tess, webUrl: "" });
                }
                else {
                    directories.push(sharepointItem);
                }
            }
        } else if (rootItem.isTeam) {
            directories.push(teamsItem);
        }
    }

    for (let index = 0; index < data.length; index++) {
        const item = data[index];
        if (item.treeType === DirectoryTreeType.location) {
            directories.push({
                type: item.type,
                id: item.id,
                directory: "",
                displayName: item.name,
                webUrl: item.type === ComponentTypes.OneDrive ? user.oneDriveWebUrl : "",
                sharePointListId: ""
            });
        }
        else if (item.treeType === DirectoryTreeType.site) {
            if (item.isTeam) {
                currentDirectory = directories[directories.length - 1];

                directories.push({
                    id: item.id,
                    type: ComponentTypes.SharePointDocuments,
                    siteId: item.siteId,
                    siteDisplayName: item.name,
                    webUrl: item.webUrl,
                    directory: "",
                    DocumentLibrary: "",
                    teamGroupId: item.teamGroupId,
                    teamDriveId: item.teamDriveId,
                    isSharedTeam: item.isSharedTeam,
                    displayName: item.name,
                    isTeamsFolder: true,
                    isHub: item.isHub,
                    hubId: item.hubId,
                    parentHubId: item.parentHubId
                });
            } else {
                directories.push({
                    id: item.id,
                    type: ComponentTypes.SharePointDocuments,
                    siteId: item.id,
                    siteDisplayName: item.name,
                    webUrl: item.webUrl,
                    directory: "",
                    DocumentLibrary: "",
                    displayName: item.name,
                    isHub: item.isHub,
                    hubId: item.hubId,
                    parentHubId: item.parentHubId
                });
            }
        }
        else if (item.treeType === DirectoryTreeType.document) {
            currentDirectory = directories[directories.length - 1];

            let siteId: string;
            if (currentDirectory.type === ComponentTypes.TessAccount || currentDirectory.type === ComponentTypes.TessProject) {
                siteId = item.siteId;
            }
            else if (currentDirectory.isTeamsFolder) {
                siteId = currentDirectory.siteId;
            }
            else {
                siteId = currentDirectory.id;
            }

            if (item.isList) {
                directories.push({
                    id: item.id,
                    type: ComponentTypes.SharePointList,
                    siteId,
                    siteDisplayName: currentDirectory.siteDisplayName,
                    webUrl: currentDirectory.webUrl || item.siteUrl,
                    fileTitle: item.name,
                    directory: getLastSplittedStringItem(item.webUrl, /[\s/]+/),
                    sharepointList: item.name,
                    displayName: item.name,
                    isTeamsFolder: currentDirectory.isTeamsFolder,
                    sharePointListId: item.sharePointListId
                });
            }
            else {
                directories.push({
                    id: item.id,
                    type: ComponentTypes.SharePointFolders,
                    siteId,
                    siteDisplayName: currentDirectory.siteDisplayName,
                    webUrl: currentDirectory.webUrl || item.siteUrl,
                    fileTitle: item.name,
                    directory: getLastSplittedStringItem(item.webUrl, /[\s/]+/),
                    DocumentLibrary: item.name,
                    displayName: item.name,
                    isTeamsFolder: currentDirectory.isTeamsFolder,
                    sharePointListId: item.sharePointListId
                });
            }
        }
        else if (item.treeType === DirectoryTreeType.channel) {
            currentDirectory = directories[directories.length - 1];
            teamChannelInfo = await getTeamChannelInfo({ channelId: item.id, driveId: item.driveId, groupId: item.teamGroupId, isPrivate: item.isPrivate, isShared: item.isShared });
            if (teamChannelInfo) {
                directories.push({
                    id: item.isShared ? teamChannelInfo.driveId : teamChannelInfo.driveItemId,
                    parentId: item.isShared ? null : teamChannelInfo.driveId,
                    type: ComponentTypes.SharePointFolders,
                    siteDisplayName: currentDirectory.siteDisplayName,
                    webUrl: teamChannelInfo.sharePointSiteUrl,
                    fileTitle: item.displayName,
                    directory: getRelativeDocumentUrl(teamChannelInfo.driveItemWebUrl, item.isShared),
                    DocumentLibrary: teamChannelInfo.documentLibraryName,
                    displayName: item.displayName,
                    siteId: teamChannelInfo.siteId,
                    isTeamsRootFolder: true,
                    isTeamsFolder: currentDirectory.isTeamsFolder,
                    isSharedTeamsChannel: item.isShared,
                    sharePointListId: teamChannelInfo.sharePointListId,
                    teamChannelId: item.id
                });
            }
        }
        else if (item.treeType === DirectoryTreeType.folder && !item.isOneDrive) {
            currentDirectory = directories[directories.length - 1];
            directories.push({
                id: item.id,
                parentId: item.driveId,
                type: ComponentTypes.SharePointFolders,
                siteId: currentDirectory.siteId,
                siteDisplayName: currentDirectory.siteDisplayName,
                webUrl: currentDirectory.webUrl || item.webUrl,
                fileTitle: item.name,
                directory: item.path ? tryGetSiteRelativeUrl(currentDirectory.webUrl || item.webUrl, item.path) : `${currentDirectory.directory}/${item.name}`,
                DocumentLibrary: currentDirectory.DocumentLibrary,
                displayName: item.name,
                isTeamsFolder: currentDirectory.isTeamsFolder,
                sharePointListId: item.sharePointListId ?? currentDirectory.sharePointListId,
                contentTypeId: item.contentTypeId,
                serverRelativePath: item.path,
                sharePointUniqueId: item.sharePointUniqueId
            });
        }
        else if (item.treeType === DirectoryTreeType.folder && item.isOneDrive) {
            currentDirectory = directories[directories.length - 1];
            directories.push({
                id: item.id,
                parentId: item.parentId || item.driveId,
                type: item.level === 1 ? ComponentTypes.OneDriveSubFolder : ComponentTypes.OneDriveSubFolder,
                webUrl: item.webUrl,
                fileTitle: item.name,
                directory: `${currentDirectory.directory ? `${currentDirectory.directory}/${item.name}` : `${item.name}`}`,
                DocumentLibrary: currentDirectory.DocumentLibrary,
                displayName: item.name,
                sharePointListId: item.sharePointListId,
                contentTypeId: item.contentTypeId,
                serverRelativePath: item.path,
                sharePointUniqueId: item.sharePointUniqueId
            });

            if (!directories[0].sharePointListId) {
                directories[0].sharePointListId = item.sharePointListId;
            }
        }
        else if (item.treeType === DirectoryTreeType.tessAccount) {
            directories.push({
                id: item.id,
                type: ComponentTypes.TessAccount,
                siteId: item.id,
                siteDisplayName: item.name,
                webUrl: item.webUrl,
                directory: "",
                DocumentLibrary: "",
                displayName: item.name,
                isHub: item.isHub,
                hubId: item.hubId,
                parentHubId: item.parentHubId,
                tessAccountId: item.accountId
            });
        }
        else if (item.treeType === DirectoryTreeType.tessProject) {
            directories.push({
                id: item.id,
                type: ComponentTypes.TessProject,
                displayName: item.name,
                tessAccountId: item.accountId,
                tessProjectId: item.tessProjectId
            });
        }
    }

    return { directories, teamChannelInfo };
}

export const openTreeFolderOptions = async (locations: any[], location: any, user: UserState): Promise<IFolderPanelInfo | undefined> => {
    const newWorkingDirectories = findWorkingDirectories(locations, location);
    let { directories, teamChannelInfo } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
    const currentDirectory = directories[directories.length - 1];

    if (location.isTeam) {
        return Promise.resolve({
            isOpen: true,
            folder: { driveId: null, name: location.name, id: location.id, siteId: location.siteId, isSharedTeam: location.isSharedTeam },
            folderType: FolderTypeEnum.teamMainSite,
            webUrl: location.webUrl,
            locations: directories
        })
    }
    if (location.isSite || location.isSubsite || location.isHub) {
        return Promise.resolve({
            isOpen: true,
            folder: { driveId: null, name: location.name, id: location.id },
            folderType: location.isHub ? FolderTypeEnum.sharepointHubSite : FolderTypeEnum.sharepointSite,
            webUrl: location.webUrl,
            locations: directories
        })
    }
    if (location.treeType === DirectoryTreeType.document) {
        return Promise.resolve({
            isOpen: true,
            folder: { driveId: location.id, name: location.name, id: null, sharePointListId: location.sharePointListId, isList: location.isList },
            folderType: location.isList ? FolderTypeEnum.sharepointList : FolderTypeEnum.sharepoint,
            siteId: location.siteId,
            webUrl: location.webUrl,
            locations: directories
        })
    }
    if (location.treeType === DirectoryTreeType.channel) {
        return Promise.resolve({
            isOpen: true,
            folder: { driveId: teamChannelInfo.driveId, name: location.displayName, id: location.isShared ? null : teamChannelInfo.driveItemId },
            folderType: FolderTypeEnum.teamChannel,
            webUrl: location.webUrl,
            locations: directories
        })
    }
    if (location.treeType === DirectoryTreeType.folder && !location.isOneDrive) {
        return Promise.resolve({
            isOpen: true,
            folder: { driveId: location.driveId, name: location.name, id: location.id, sharePointListId: location.sharePointListId },
            folderType: FolderTypeEnum.sharepoint,
            webUrl: location.webUrl,
            locations: directories
        })
    }
    if (location.treeType === DirectoryTreeType.folder && location.isOneDrive) {
        return Promise.resolve({
            isOpen: true,
            folder: {
                driveId: location.driveId || location.parentId,
                name: location.name,
                id: location.id,
                webUrl: `${location.webUrl}/${currentDirectory.directory}`,
                //sharePointListId: location.sharePointListId 
            },
            folderType: FolderTypeEnum.oneDriveSubFolder,
            webUrl: location.webUrl,
            locations: directories
        })
    }
    if (location.treeType === DirectoryTreeType.tessProject) {
        return Promise.resolve({
            isOpen: true,
            folder: { driveId: null, name: location.name, id: location.id },
            folderType: FolderTypeEnum.sharepointSite,
            webUrl: location.webUrl,
            locations: directories,
            tessAccountId: location.accountId,
            tessProjectId: location.tessProjectId
        })
    }

    return undefined;
};

export const calculateLocationHeight = (locations: any) => {
    const itemHeight = isMobile() ? 55 : 40;
    const countVisibleItems = (node) => {
        let count = 1;
        if (node.expanded) {
            node.sites?.forEach(site => {
                count += countVisibleItems(site);
            });

            node.accounts?.forEach(account => {
                count += countVisibleItems(account);
            });

            node.projectFolders?.forEach(projectFolder => {
                count += countVisibleItems(projectFolder);
            });

            node.projects?.forEach(project => {
                count += countVisibleItems(project);
            });

            node.subSites?.forEach(subSite => {
                count += countVisibleItems(subSite);
            });

            node.folders?.forEach(folder => {
                if (folder.foldersNextHref) {
                    count += 1;
                }
                count += countVisibleItems(folder);
            });

            node.documents?.forEach(doc => {
                count += 1;
                if (doc.foldersNextHref) {
                    count += 1;
                }
                doc.folders?.forEach(folder => {
                    count += countVisibleItems(folder);
                });
            });

            node.channels?.forEach((chan) => {
                count += 1;
                if (chan.foldersNextHref) {
                    count += 1;
                }
                chan.folders?.forEach(folder => {
                    count += countVisibleItems(folder);
                });
            });

            node.folders?.forEach(() => {
                count += 1;
            });

            if (node.actions?.length) {
                count += node.actions.length;
            }

            if (node.message) {
                count += 1;
            }
        }

        return count;
    };

    const visibleItemCount = locations?.reduce((count, location) => {
        return count + countVisibleItems(location);
    }, 0);

    return visibleItemCount * itemHeight;
}

const radonStringCharList: string = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
export const generateRandomString = (length: number = 15) => {
    let result = "";
    for (let i = length; i > 0; --i) {
        result += radonStringCharList[Math.floor(Math.random() * radonStringCharList.length)];
    }
    return result;
}

export const treeViewItemStyle = (location: any): CSSProperties => {
    let leftPad: number = location.level * 26;
    if (!location.isLoading && ((location.loaded && !hasChildren(location)) || location.canExpand === false)) {
        leftPad += 10 + getIconSizeOffset(location);
    }

    return {
        display: "flex",
        alignItems: "center",
        paddingTop: 13,
        paddingBottom: 13,
        paddingRight: location.isLoading ? 12 : 10,
        paddingLeft: leftPad
    }
}

export const hasChildren = (location: any) => {
    if (location.sites?.length > 0
        || location.subSites?.length > 0
        || location.folders?.length > 0
        || location.documents?.length > 0
        || location.channels?.length > 0
        || location.accounts?.length > 0
        || location.projectFolders?.length > 0
        || location.projects?.length > 0
        || location.message) {
        return true;
    }

    return false;
}

const getIconSizeOffset = (location: any) => {
    if (location.type === ComponentTypes.Exchange
        || location.type === ComponentTypes.Teams
        || location.type === ComponentTypes.SharePoint
        || location.type === ComponentTypes.OneDrive
        || location.type === ComponentTypes.Tess) {
        return -6;
    }

    // if (location.treeType === DirectoryTreeType.folder || location.treeType === DirectoryTreeType.document || location.treeType === DirectoryTreeType.channel) {
    //     return 4;
    // }

    // return 0;
    return 4;
}